import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";
import {
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Twitch,
  Twitter,
  Youtube,
} from "react-feather";
import { useFormContext } from "react-hook-form";

import BoxCard from "@/components/common/BoxCard";
import SelectAdvanced from "@/components/common/SelectAdvanced";

const MySocialMedia = (): React.ReactElement => {
  const { register, watch } = useFormContext();
  const { t } = useTranslation("common");
  const [
    twitter,
    facebook,
    instagram,
    youtube,
    twitch,
    linkedin,
    tiktok,
    otherSocial,
  ] = watch([
    "twitter",
    "facebook",
    "instagram",
    "youtube",
    "twitch",
    "linkedin",
    "tiktok",
    "otherSocial",
  ]);
  const [socialMedia, updateSocialMedia] = useState([]);
  const showSocialMedia = useMemo(
    () => ({
      twitter: socialMedia?.some((media) => media?.value === 0),
      facebook: socialMedia?.some((media) => media?.value === 1),
      instagram: socialMedia?.some((media) => media?.value === 2),
      youtube: socialMedia?.some((media) => media?.value === 3),
      twitch: socialMedia?.some((media) => media?.value === 4),
      linkedin: socialMedia?.some((media) => media?.value === 5),
      tiktok: socialMedia?.some((media) => media?.value === 6),
      otherSocial: socialMedia?.some((media) => media?.value === 7),
    }),
    [socialMedia]
  );

  const options = useMemo(
    () => [
      {
        value: 0,
        label: (
          <Flex align={"center"}>
            <Icon as={Twitter} mr={2} />
            <Text>Twitter</Text>
          </Flex>
        ),
      },
      {
        value: 1,
        label: (
          <Flex align={"center"}>
            <Icon as={Facebook} mr={2} />
            <Text>Facebook</Text>
          </Flex>
        ),
      },
      {
        value: 2,
        label: (
          <Flex align={"center"}>
            <Icon as={Instagram} mr={2} />
            <Text>Instagram</Text>
          </Flex>
        ),
      },
      {
        value: 3,
        label: (
          <Flex align={"center"}>
            <Icon as={Youtube} mr={2} />
            <Text>Youtube</Text>
          </Flex>
        ),
      },
      {
        value: 4,
        label: (
          <Flex align={"center"}>
            <Icon as={Twitch} mr={2} />
            <Text>Twitch</Text>
          </Flex>
        ),
      },
      {
        value: 5,
        label: (
          <Flex align={"center"}>
            <Icon as={Linkedin} mr={2} />
            <Text>Linkedin</Text>
          </Flex>
        ),
      },
      {
        value: 6,
        label: (
          <Flex align={"center"}>
            <svg viewBox="0 0 551.93506 551.93505" width="14" height="14">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="40"
                d="m 499.95276,229.90001 a 210.1,210.1 0 0 1 -122.8,-39.25 v 178.75 a 162.6,162.6 0 1 1 -140.2,-161.1 v 89.9 a 74.62,74.62 0 1 0 52.23,71.18 v -349.38 h 88 a 121.2,121.2 0 0 0 1.86,22.17 v 0 a 122.2,122.2 0 0 0 53.91,80.23 121.4,121.4 0 0 0 67,20.14 z"
              />
            </svg>
            <Text ml={2}>TikTok</Text>
          </Flex>
        ),
      },
      {
        value: 7,
        label: (
          <Flex align={"center"}>
            <Icon as={Globe} mr={2} />
            <Text>{t("other")}</Text>
          </Flex>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (twitter)
      updateSocialMedia((socialMedia) => [...socialMedia, options[0]]);

    if (facebook)
      updateSocialMedia((socialMedia) => [...socialMedia, options[1]]);

    if (instagram)
      updateSocialMedia((socialMedia) => [...socialMedia, options[2]]);

    if (youtube)
      updateSocialMedia((socialMedia) => [...socialMedia, options[3]]);

    if (twitch)
      updateSocialMedia((socialMedia) => [...socialMedia, options[4]]);

    if (linkedin)
      updateSocialMedia((socialMedia) => [...socialMedia, options[5]]);

    if (tiktok)
      updateSocialMedia((socialMedia) => [...socialMedia, options[6]]);

    if (otherSocial)
      updateSocialMedia((socialMedia) => [...socialMedia, options[7]]);
  }, [
    twitter,
    facebook,
    youtube,
    instagram,
    twitch,
    linkedin,
    tiktok,
    otherSocial,
  ]);

  return (
    <>
      <BoxCard p={0} mt={3} borderRadius={".375rem"}>
        <Box p="1.25rem" borderBottomColor="light.50" borderBottomWidth="1px">
          <Flex
            justifyContent={"space-between"}
            align={{ base: "flex-start", md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
          >
            <Heading as="h3" size="md" mr={2}>
              {t("socialMedia.mySocialMedia")}
            </Heading>
            <Box
              width={{ base: "100%", md: "60%" }}
              mt={{ base: 2, md: undefined }}
            >
              <SelectAdvanced
                isMulti
                options={options}
                value={socialMedia}
                onChange={(values) => updateSocialMedia(values)}
              />
            </Box>
          </Flex>
        </Box>
        <Box p="1.25rem">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={5} spacingY={3}>
            {showSocialMedia.twitter && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="twitter">Twitter</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Twitter} />
                    </InputLeftAddon>
                    <Input type="text" {...register("twitter")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.facebook && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="facebook">Facebook</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Facebook} />
                    </InputLeftAddon>
                    <Input type="text" {...register("facebook")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.instagram && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="instagram">Instagram</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Instagram} />
                    </InputLeftAddon>
                    <Input type="text" {...register("instagram")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.youtube && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="youtube">YouTube</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Youtube} />
                    </InputLeftAddon>
                    <Input type="text" {...register("youtube")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.twitch && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="twitch">Twitch</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Twitch} />
                    </InputLeftAddon>
                    <Input type="text" {...register("twitch")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.linkedin && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="linkedin">LinkedIn</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Linkedin} />
                    </InputLeftAddon>
                    <Input type="text" {...register("linkedin")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.tiktok && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="tiktok">TikTok</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <svg
                        viewBox="0 0 551.93506 551.93505"
                        width="14"
                        height="14"
                      >
                        <path
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="40"
                          d="m 499.95276,229.90001 a 210.1,210.1 0 0 1 -122.8,-39.25 v 178.75 a 162.6,162.6 0 1 1 -140.2,-161.1 v 89.9 a 74.62,74.62 0 1 0 52.23,71.18 v -349.38 h 88 a 121.2,121.2 0 0 0 1.86,22.17 v 0 a 122.2,122.2 0 0 0 53.91,80.23 121.4,121.4 0 0 0 67,20.14 z"
                        />
                      </svg>
                    </InputLeftAddon>
                    <Input type="text" {...register("tiktok")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
            {showSocialMedia.otherSocial && (
              <FormControl>
                <Box>
                  <FormLabel htmlFor="other">{t("other")}</FormLabel>
                  <InputGroup>
                    <InputLeftAddon>
                      <Icon as={Globe} />
                    </InputLeftAddon>
                    <Input type="text" {...register("otherSocial")} />
                  </InputGroup>
                </Box>
              </FormControl>
            )}
          </SimpleGrid>
        </Box>
      </BoxCard>
    </>
  );
};

export default MySocialMedia;
